import { configureStore } from "@reduxjs/toolkit";
import type { PreloadedState } from "@reduxjs/toolkit";

import { RootState, rootReducer } from "./rootReducer";
import { persistStore } from "redux-persist";
import { setActiveClinicTimezone } from "../util/clinicTimezone";

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }),
});

/**
 * Mirror a store's active clinic IANA timezone into the clinicTimezone
 * singleton so non-React code (utils, firebase cache, driven adapters) can
 * interpret/format timestamps in clinic time. Seeds immediately from the
 * current (possibly rehydrated/preloaded) state, then updates on change —
 * writing only when the value actually changes to avoid churn. Falls back to
 * browser TZ when unknown. Applied to both the production store and any store
 * created via `setupStore` (tests/SSR) so clinic-TZ behavior isn't browser-TZ.
 */
const attachClinicTimezoneSync = (s: {
  getState: () => unknown;
  subscribe: (listener: () => void) => unknown;
}) => {
  let lastClinicTz: string | undefined;
  const sync = () => {
    const tz = (s.getState() as RootState)?.clinic?.userClinic?.timezone;
    if (tz !== lastClinicTz) {
      lastClinicTz = tz || undefined;
      setActiveClinicTimezone(lastClinicTz);
    }
  };
  sync();
  s.subscribe(sync);
};

attachClinicTimezoneSync(store);

/**
 * Diagnostics for the intermittent "device shows as unpaired" reports. Watches
 * `clinic.assignedDevice` and traces every null↔set flip (the exact moment the
 * member home banner shows/hides "No device connected yet"), regardless of which
 * code path caused it (poll / bootstrap / pull-to-refresh / removal). Emitted to
 * Sentry + Mixpanel via a lazy import so this module never statically pulls in
 * the AppController/analytics graph. Logging is best-effort and never throws.
 */
const attachAssignedDeviceTracing = (s: {
  getState: () => unknown;
  subscribe: (listener: () => void) => unknown;
}) => {
  const readDeviceId = () =>
    (s.getState() as RootState)?.clinic?.assignedDevice?.device_id ?? null;
  let lastDeviceId: string | null = readDeviceId();
  const attachedAt = Date.now();
  s.subscribe(() => {
    const state = s.getState() as RootState;
    const deviceId = state?.clinic?.assignedDevice?.device_id ?? null;
    if (deviceId === lastDeviceId) return;
    const from = lastDeviceId;
    lastDeviceId = deviceId;
    const payload = {
      from,
      to: deviceId,
      becameUnpaired: !!from && !deviceId,
      becamePaired: !from && !!deviceId,
      clinicId: state?.clinic?.userClinic?.id ?? null,
      patientId: state?.auth?.user?.patient_id ?? null,
      email: state?.auth?.user?.email ?? null,
      deviceConnStatus: state?.clinic?.assignedDevice?.device_conn_status ?? null,
      // Small value => likely the redux-persist rehydrate at app start, not a
      // real in-session flip. Lets you filter startup noise from true toggles.
      msSinceAttach: Date.now() - attachedAt,
    };
    void import("../util/deviceTrace")
      .then(({ traceDeviceEvent }) =>
        traceDeviceEvent("assigned_device_transition", payload)
      )
      .catch(() => {});
  });
};

attachAssignedDeviceTracing(store);

export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  const configuredStore = configureStore({
    reducer: rootReducer,
    preloadedState,
  });
  attachClinicTimezoneSync(configuredStore);
  return configuredStore;
};

export type AppDispatch = typeof store.dispatch;

export const persistedStore = persistStore(store);

export type AppStore = ReturnType<typeof setupStore>;

export default store;
